import React, {
	useRef,
	useCallback,
	useMemo,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
} from 'react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FIELD_TYPE_MAP } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { getIsEditFromIssueView } from '@atlassian/jira-issue-analytics/src/utils/get-is-edit-from-issue-view/index.tsx';
import {
	useCanConfigureStatus,
	shouldShowConfigureStatusOption,
} from '@atlassian/jira-status-config-common/src/permissions.tsx';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import { useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';

import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useViewMode } from '@atlassian/jira-issue-context-service/src/main.tsx';
import type { StatusValue } from '../common/types.tsx';
import { StatusService } from '../services/status-service/index.tsx';
import { TransitionsQuery } from '../services/transitions-query/index.tsx';
import ViewWorkflowContextServiceDI from '../services/view-workflow-service/index.tsx';
import type { OnStatusChangeSuccess } from '../services/status-service/types.tsx';
import StatusView from './status-view/index.tsx';
import type { Props } from './types.tsx';
import ViewWorkflow from './view-workflow/index.tsx';

export const StatusField = ({
	isOptimistic = true,
	ViewWorkflowContextService = ViewWorkflowContextServiceDI,
	preFetchTransitions = false,
	projectType = null,
	onEditStart = noop,
	onEditCancel = noop,
	onSubmit = noop,
	onSuccess = noop,
	onFailure = noop,
	onModalClose = noop,
	issueKey,
	issueId,
	isEditable,
	initialValue,
	appearance,
	value,
	statusButtonRef: statusButtonRefExternal,
	popperProps,
	registerInCommandPalette,
	onUpdateStatusMutation,
	isStatusOnLHS,
	onEditedStatusDetails,
	shouldCaptureEscape,
}: Props): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const projectKey = useProjectKey(issueKey);
	const canConfigureStatus = useCanConfigureStatus(projectKey);
	const showConfigureStatusButtons =
		canConfigureStatus && shouldShowConfigureStatusOption(projectType);

	const statusButtonRef = useRef<HTMLElement>(null);

	const tenantContext = useTenantContext();
	const viewMode = useViewMode();

	const onCloseModal = useCallback(() => {
		statusButtonRef?.current?.focus();
		onModalClose();
	}, [onModalClose]);

	const shouldNotRender = useMemo(() => {
		return !tenantContext || !(value || initialValue);
	}, [tenantContext, value, initialValue]);

	const restoreFocus = useCallback(() => {
		setTimeout(() => {
			const element = globalThis.document?.getElementById('issue.fields.status-view.status-button');
			if (element instanceof HTMLElement) {
				element.focus();
			}
		}, 500);
	}, []);

	const successCallback = useCallback(
		(...params: Parameters<OnStatusChangeSuccess>) => {
			onSuccess(...params);
			// restore focus back to status button after successfully changing the status
			restoreFocus();
		},
		[onSuccess, restoreFocus],
	);

	const failureCallback = useCallback(
		(err?: Error) => {
			onFailure(err);
			// restore focus back to status button after changing the status fails
			restoreFocus();
		},
		[onFailure, restoreFocus],
	);

	const pressTrace = usePressTracing('issue.field.status');

	return shouldNotRender ? null : (
		<StatusService
			appearance={appearance}
			value={value}
			projectType={projectType}
			initialValue={initialValue}
			issueKey={issueKey}
			issueId={issueId}
			onSubmit={onSubmit}
			onSuccess={successCallback}
			onFailure={failureCallback}
			onEditStart={onEditStart}
			onEditCancel={onEditCancel}
			checkRefreshedData
			{...(fg('relay-migration-issue-fields-status')
				? {
						onUpdateStatusMutation,
					}
				: {})}
		>
			{({ value: statusServiceValue, loading: saving, transitionStatus, error, resetError }) => (
				<TransitionsQuery
					issueKey={issueKey}
					// this will force an empty list of transitions when the status changes
					status={statusServiceValue}
					isSaving={saving}
					preFetch={preFetchTransitions}
					projectType={projectType}
				>
					{({ data: transitions, loading, fetch, ui }) => (
						<ViewWorkflowContextService issueKey={issueKey}>
							{(workflowServiceApi) => {
								const isSaving = saving || Boolean(ui?.transitionInProgress);

								const viewWorkflowFooter = (
									<JSErrorBoundary id="issue-field-view-workflow">
										<ViewWorkflow
											issueKey={issueKey}
											workflowServiceApi={workflowServiceApi}
											onCloseModal={onCloseModal}
										/>
									</JSErrorBoundary>
								);

								const workflowFooter = <>{viewWorkflowFooter}</>;

								return (
									<>
										<StatusView
											statusButtonRef={statusButtonRefExternal || statusButtonRef}
											value={statusServiceValue}
											isEditable={isEditable}
											isLoading={loading || saving || Boolean(ui?.transitionInProgress)}
											isSaving={isOptimistic === true ? false : isSaving}
											appearance={appearance}
											transitions={transitions}
											onOpen={(analyticsEvent: UIAnalyticsEvent) => {
												onEditStart();

												if (fg('fix_blindspots_for_board_and_backlog')) {
													pressTrace();
												}

												const startEvent = createAnalyticsEvent({});
												getUpdateAnalyticsFlowHelper().fireAnalyticsStart(STATUS_TYPE, {
													analytics: startEvent,
													attributes: {
														fieldType: FIELD_TYPE_MAP[STATUS_TYPE],
														isInlineEditing: !getIsEditFromIssueView(startEvent),
													},
												});

												fetch(analyticsEvent);

												const hasFetchedWorkflowService =
													workflowServiceApi.data &&
													workflowServiceApi.data.hasPermission !== undefined;
												if (hasFetchedWorkflowService !== true) {
													workflowServiceApi.fetch();
												}
												resetError();
											}}
											onClose={onEditCancel}
											onChange={transitionStatus}
											shouldCaptureEscape={shouldCaptureEscape}
											error={error}
											footer={workflowFooter}
											popperProps={popperProps}
											registerInCommandPalette={registerInCommandPalette}
											onSuccess={onSuccess}
											onFetch={fetch}
											{...(fg('relay-migration-issue-fields-status')
												? {
														onUpdateStatusMutation,
													}
												: {})}
											showConfigureStatusButtons={showConfigureStatusButtons}
											configureStatusDialogType={viewMode === 'SIDEBAR' ? 'modal' : 'popup'}
											configureStatusPopupPlacement={isStatusOnLHS ? 'bottom-start' : 'bottom-end'}
											projectId={workflowServiceApi.data?.projectId}
											issueTypeId={workflowServiceApi.data?.issueTypeId}
											projectKey={projectKey}
											projectType={projectType}
											onStatusCreatedOrUpdated={async (options) => {
												const { shouldRefreshCurrentStatus } = options || {};
												// Refetch transitions
												fetch(createAnalyticsEvent({}));
												// Refetch current status if details have been updated
												if (shouldRefreshCurrentStatus) {
													onEditedStatusDetails?.({});
												}
											}}
										/>
									</>
								);
							}}
						</ViewWorkflowContextService>
					)}
				</TransitionsQuery>
			)}
		</StatusService>
	);
};

export default StatusField;

/**
 * We had to add these here to avid circular dependency.
 * The issue here is StatusField component has some defaultProps which were not included as optional in types earlier
 * So we created a new prop to be used from async file.
 */
type BaseProps = JSX.LibraryManagedAttributes<
	typeof StatusField,
	ComponentProps<typeof StatusField>
>;
export type ExpProps = BaseProps & {
	initialValue?: StatusValue;
	value?: StatusValue;
	statusButtonRef?: {
		current: HTMLElement | null;
	};
};
